import { useMemo } from 'react';
import { getThemeByTenantId } from '@tenants';
import { BadgeDisplayPolicy, BadgeGroup, useBadges } from 'ui/badges';

import type { TenantId } from '@tenants/types';
import type Listing from 'data/listing';
import type { GallerySectionData } from './types';

/**
 * useGallerySection
 * 
 * Hook that prepares listing images for gallery section.
 * No formatter needed - just data preparation (no business logic or i18n).
 * 
 * @param listing - Raw listing data
 * @param badges - Optional badge overlay component
 * @returns Gallery data ready for ListingGallerySection
 * 
 * @example
 * ```tsx
 * const galleryData = useGallerySection(listing, <BadgeOverlay />);
 * return <ListingGallerySection data={galleryData} variant="carousel" />;
 * ```
 */
export function useGallerySection(
  listing: Listing,
  badges?: React.ReactNode
): GallerySectionData {
  return useMemo(() => ({
    images: listing.imageUrls || [],
    descriptions: null, // TODO: Add image descriptions support
    total: listing.imageUrls?.length || 0,
    badges,
    altPrefix: listing.streetAddress,
  }), [listing.imageUrls, listing.streetAddress, badges]);
}

/**
 * useGalleryWithBadges
 * 
 * Hook that enhances gallery data with listing badges based on tenant theme.
 * Combines useGallerySection with badge display logic.
 * 
 * @param galleryData - Base gallery section data
 * @param tenantId - Tenant identifier for theme colors
 * @param listing - Listing data for badge selection
 * @returns Gallery data with badges overlay
 * 
 * @example
 * ```tsx
 * const baseGallery = useGallerySection(listing);
 * const galleryWithBadges = useGalleryWithBadges(baseGallery, tenantId, listing);
 * return <ListingGallerySection data={galleryWithBadges} />;
 * ```
 */
export function useGalleryWithBadges(
  galleryData: GallerySectionData,
  tenantId: TenantId,
  listing: Listing
): GallerySectionData {
  const theme = getThemeByTenantId(tenantId);
  const badgePolicy = new BadgeDisplayPolicy();
  const badgeSelections = badgePolicy.selectBadges(listing);

  const { badges, hasBadges } = useBadges({
    selections: badgeSelections,
    primaryColor: theme.colors.primaryDark,
  });

  return {
    ...galleryData,
    badges: hasBadges ? <BadgeGroup badges={badges} position="top-left" /> : undefined,
  };
}
